import { Concept } from 'clarifai-web-grpc/proto/clarifai/api/resources_pb';
import { AnnoUiRegionItem } from 'utils/cfUiItems/annotations';
import { numbersToSymbol, symbolsToNumber } from 'modules/KeyboardShortcuts/utils';
import { ListenerAction } from 'modules/InputViewer/components/Sidebar/Annotations/types';
import { grpcToRestConcept } from 'utils/grpc/grpcToRest';
import { getConceptsFromAnnotationData } from 'utils/annotations/general';
import { getRegionData } from 'modules/TaskManager/components/TaskSidebar/TaskRegions/RegionAnnotation/utils';
import { hasRegionsWithConcepts } from 'components/ModelOutputRenderer/components/utils';
import { stringToColor } from 'utils/strings/strings';

export const extractIds = (itemKey: string) => {
  const splitKey = itemKey.split('-');
  const regionId = splitKey[splitKey.length - 1];
  const conceptId = splitKey.slice(0, -1).join('-');

  return { regionId, conceptId };
};

export const getAnnotationItemByRegionId = (item: AnnoUiRegionItem, regionId: string) => item.key.includes(regionId);

export const getAnnotationItemByConceptId = (item: AnnoUiRegionItem, conceptId: string) => item.concepts?.some((concept) => concept.id === conceptId);

export const getAnnotationItemByConceptName = (item: AnnoUiRegionItem, conceptName: string) =>
  item.concepts?.some((concept) => concept.name === conceptName);

export const getAnnotationItemByUserId = (item: AnnoUiRegionItem, userId: string) => {
  const { worker } = item.annotation || {};
  const { user } = worker || {};

  return user?.id?.length && userId.length ? user.id === userId : false;
};

export const getFilteredWorkflowData = (
  workflowData: CF.API.WorkflowOutputs.WorkflowOutput[],
  findConceptByName: (v: string) => Concept | undefined,
) =>
  workflowData.map((wd: CF.API.WorkflowOutputs.WorkflowOutput) => {
    const regions = (wd.data as CF.API.BaseRegionsData)?.regions || [];
    if (regions) {
      const newRegions = regions
        .filter((r) => r?.data?.concepts)
        .map((r: CF.API.RegionsEntity) => {
          const concepts = r?.data?.concepts || ([] as CF.API.Concepts.Concept[]);
          const newConcepts = concepts.map((c) => {
            const existingConcept = findConceptByName(c.name);
            return existingConcept ? { ...grpcToRestConcept(existingConcept), value: c.value } : c;
          });
          return { ...r, data: { ...r.data, concepts: newConcepts } };
        });
      if (newRegions) {
        return {
          ...wd,
          data: {
            ...wd.data,
            regions: newRegions,
          },
        };
      }
    }
    return wd;
  });

/**
 *
 * @param strings - Array of strings to format : [sagar, leo, nik, sarath]
 * @param maxStrings
 * @param maxLength
 * @returns - Formatted string array : "sagar, leo, nik, +1"
 */
export function formatStringArray(
  strings: string[],
  maxStrings: number = 3,
  maxLength: number = 10,
): {
  strings: string;
  remainingCount: number;
} {
  // Step 1: Truncate each string to the specified maxLength
  const truncatedStrings = strings.map((str) => {
    return str.length > maxLength ? `${str.slice(0, maxLength)}..., ` : `${str}, `;
  });

  // Step 2: Get the first 'maxStrings' elements
  const selectedStrings = truncatedStrings.slice(0, maxStrings);

  if (truncatedStrings.length <= maxStrings) {
    // remove comma from last element
    selectedStrings[truncatedStrings.length - 1] = selectedStrings[truncatedStrings.length - 1].slice(0).replace(',', '');
  }

  // Step 3: Check if there are more strings than the 'maxStrings' limit
  let remainingCount = 0;
  if (strings.length > maxStrings) {
    remainingCount = strings.length - maxStrings;
  }

  return { strings: selectedStrings.join(''), remainingCount };
}

/**
 * Finds the annotation for a given user ID.
 * Falls back to the first annotation if none match the user.
 */
export const getAnnotationByUserId = (annotations?: CF.API.Annotations.Annotation[], userId?: string): CF.API.Annotations.Annotation | undefined => {
  if (!annotations || annotations.length === 0) return undefined;

  return annotations.find((a) => a.worker?.user?.id === userId) || annotations[0];
};

/**
 * Extracts the concept for a given user from a single annotation.
 * Falls back to the first concept if no user-specific match is found.
 */
export const getConceptByUserId = (annotation?: CF.API.Annotations.Annotation, userId?: string): CF.API.Concept | undefined => {
  if (!annotation || !userId) return undefined;

  const concepts = getConceptsFromAnnotationData(annotation.data);

  return concepts.find((c) => c.user_id === userId) || concepts[0];
};

// /**
//  * Extracts the concept for a given user from a single annotation.
//  * Falls back to the first concept if no user-specific match is found.
//  */
// export const getConceptByConceptAndUserId = (
//   annotation?: CF.API.Annotations.Annotation,
//   conceptId?: string,
//   userId?: string,
// ): CF.API.Concept | undefined => {
//   if (!annotation || !userId || !conceptId) return undefined;

//   const concepts = getConceptsFromAnnotationData(annotation.data);

//   return concepts.find(c => c.user_id === userId && c.name === conceptId) || concepts[0];
// };

/**
 * Gets the concept for a user by searching through all annotations.
 */
export const getConceptFromAnnotationsByUserId = (annotations: CF.API.Annotations.Annotation[], userId: string): CF.API.Concept | undefined => {
  const annotation = getAnnotationByUserId(annotations, userId);

  return getConceptByUserId(annotation, userId);
};

export const getAnnotationsFromConceptName = (conceptName: string, annotations: CF.API.Annotations.Annotation[]): CF.API.Annotations.Annotation[] => {
  return annotations.filter((annotation) => {
    const concepts = getConceptsFromAnnotationData(annotation.data);

    return concepts.some((concept) => concept.name === conceptName);
  });
};

export const getConceptFromAnnotations = (conceptName: string, annotations: CF.API.Annotations.Annotation[]): CF.API.Concept | undefined => {
  let concept: CF.API.Concept | undefined;

  annotations.forEach((annotation) => {
    const concepts = getConceptsFromAnnotationData(annotation.data);

    concept = concepts.find((c) => c.name === conceptName);
  });

  return concept;
};

/**
 * Processes a set of key listeners by generating key combinations and applying an action.
 *
 * @param maxKeys - Maximum number of keys to process
 * @param action - Action to be performed for each key combination
 */
export const processKeyListeners = (maxKeys: number, action: ListenerAction): void => {
  // Generate an array of key combinations (e.g., ['0'], ['shift', '!'])
  const keyCombinations = Array.from({ length: maxKeys }, (_, i) => {
    const index = i + 1;
    const isNumberKey = index <= 10;

    if (isNumberKey) {
      const numericKey = index === 10 ? '0' : index.toString(); // e.g., '0', '1', ..., '9'
      return [numericKey];
    }

    const symbolKeyIndex = index === 20 ? 0 : index - 10; // Index for symbols
    const symbolKey = numbersToSymbol[symbolKeyIndex as keyof typeof numbersToSymbol]; // e.g., '!', '@', ...

    return ['shift', symbolKey]; // e.g., ['shift', '!'], ['shift', '@'], ...
  });

  // Apply the action to each key combination
  keyCombinations.forEach(action);
};

/**
 * Handles keyboard shortcuts for numeric keys and their shifted counterparts.
 *
 * @param event - Keyboard event triggered by the user
 * @returns Normalized key value or null if the key is not a valid numeric key
 * For example:
 * - `0` maps to `10`
 * - `1-9` retain their values
 * - `11-19` are `Shift + 1-9`
 * - `20` is `Shift + 0`
 */
export const getNormalizedKey = (event: KeyboardEvent): number | null => {
  const { key: pressedKey, shiftKey: isShiftPressed } = event;
  let numericValue: number;
  if (isShiftPressed) {
    // Map shifted key (e.g., `!`, `@`, ...) to its corresponding numeric value
    numericValue = symbolsToNumber[pressedKey as keyof typeof symbolsToNumber] ?? Number.NaN;
    // Special case for `Shift + 0`, which maps to `20`
    numericValue = numericValue === 0 ? 20 : numericValue + 10; // Adjust offset for shift keys
  } else {
    // Map unshifted numeric key (`0-9`) to its numeric value
    numericValue = +pressedKey;
  }
  if (Number.isNaN(numericValue)) return null;
  // Normalize key:
  // - `0` maps to `10`
  // - `1-9` retain their values
  // - `11-19` are `Shift + 1-9`
  // - `20` is `Shift + 0`
  return numericValue === 0 ? 10 : numericValue;
};

export const convertAnnotationToAnnoUiRegionItem = (
  annotation: CF.API.Annotations.Annotation,
  concepts?: CF.API.Concept[],
): AnnoUiRegionItem | undefined => {
  if (!annotation || !annotation.id) return undefined;

  const conceptList = concepts || getConceptsFromAnnotationData(annotation.data);

  if (!conceptList) return undefined;

  const regionData: CF.API.ShapeProps | undefined = getRegionData(annotation);
  // Update region data: previously, the UI Region Item derived its color from the region ID.
  // Now, colors are assigned based on the first concept ID associated with the region.
  const updatedRegionData = Object.assign({}, regionData, {
    color: stringToColor(conceptList[0].id),
  });

  if (!updatedRegionData || !hasRegionsWithConcepts(annotation.data)) return undefined;

  return {
    key: annotation.internalId || '',
    regionData: updatedRegionData,
    concepts: conceptList,
    annotation: {
      ...annotation,
      data: {
        ...annotation.data,
        regions: (annotation.data.regions || []).map((region) => ({
          ...region,
          data: {
            ...region.data,
            concepts: conceptList,
          },
        })),
      },
    },
  };
};
